/**
 * Hooks de TanStack Query para Autoridades.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildRepos } from "@/core/data/repos";
import { notificarUsuarioCreado, notificarCambioRol } from "@core/mail/cableado";
import { registrarAuditoria } from "@core/audit/registrarAuditoria";
import type { Usuario } from "@app-types/entities";
import type { Role } from "@constants/roles";

export function useUsuarios(tenantId: string) {
  return useQuery({
    queryKey: ["usuarios", tenantId],
    queryFn: () => buildRepos(tenantId).usuario.list(),
    staleTime: 60_000,
  });
}

export function useUsuariosActivos(tenantId: string) {
  return useQuery({
    queryKey: ["usuarios", tenantId, "active"],
    queryFn: () => buildRepos(tenantId).usuario.listActive(),
    staleTime: 60_000,
  });
}

export function useUsuariosByRole(tenantId: string, role: Role | null) {
  return useQuery({
    queryKey: ["usuarios", tenantId, "byRole", role],
    queryFn: () => (role ? buildRepos(tenantId).usuario.listByRole(role) : Promise.resolve([])),
    enabled: !!role,
  });
}

export function useUsuario(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: ["usuario", tenantId, id],
    queryFn: () => (id ? buildRepos(tenantId).usuario.get(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useCreateUsuario(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Omit<Usuario, "id" | "createdAt" | "updatedAt" | "version"> & {
        /** Email/nombre del ADMIN que está creando al usuario, para el correo AU-1. */
        _actorEmail?: string;
        _actorName?: string;
      },
    ) => {
      const { _actorEmail, _actorName, ...usuarioData } = data;
      const repo = buildRepos(tenantId).usuario;
      const id = await repo.create(usuarioData);
      const usuario = await repo.get(id);
      // Auditoría de personal: un alta de usuario define quién entra al PMIS y
      // con qué autoridad, así que debe quedar registrada igual que una
      // aprobación de negocio.
      void registrarAuditoria(tenantId, {
        usuario: _actorEmail ?? "desconocido",
        accion: "USUARIO_CREADO",
        entidad: "Usuario",
        entidadId: usuarioData.email || id,
        diff: {
          rol: usuarioData.rol,
          ...(usuarioData.rolesSecundarios?.length
            ? { rolesSecundarios: usuarioData.rolesSecundarios }
            : {}),
          ...(usuarioData.disciplina ? { disciplina: usuarioData.disciplina } : {}),
          ...(usuarioData.empresa ? { empresa: usuarioData.empresa } : {}),
        },
      });
      if (usuario) {
        void notificarUsuarioCreado({
          tenantId,
          usuario,
          actor: {
            email: _actorEmail ?? "admin",
            ...(_actorName !== undefined && { nombre: _actorName }),
          },
        });
      }
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["usuarios", tenantId] });
    },
  });
}

export function useUpdateUsuario(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      version,
      _actorEmail,
      _actorName,
    }: {
      id: string;
      data: Partial<Usuario>;
      version: number;
      /** Email/nombre del ADMIN para notificar AU-2 si cambia el rol. */
      _actorEmail?: string;
      _actorName?: string;
    }) => {
      const repo = buildRepos(tenantId).usuario;
      const antes = await repo.get(id);
      await repo.update(id, data, version);
      const despues = await repo.get(id);
      // Auditoría: el CAMBIO DE ROL es el dato crítico — es lo que otorga o
      // retira autoridad. Se registra siempre, con el rol anterior y el nuevo,
      // para que ningún cambio de permisos ocurra sin dejar rastro de quién.
      if (antes && despues) {
        const cambios: Record<string, unknown> = {};
        if (antes.rol !== despues.rol) {
          cambios["rolAnterior"] = antes.rol;
          cambios["rolNuevo"] = despues.rol;
        }
        const secAntes = (antes.rolesSecundarios ?? []).join(",");
        const secDespues = (despues.rolesSecundarios ?? []).join(",");
        if (secAntes !== secDespues) {
          cambios["rolesSecundariosAnterior"] = antes.rolesSecundarios ?? [];
          cambios["rolesSecundariosNuevo"] = despues.rolesSecundarios ?? [];
        }
        if (antes.activo !== despues.activo) {
          cambios["activoAnterior"] = antes.activo;
          cambios["activoNuevo"] = despues.activo;
        }
        if (antes.disciplina !== despues.disciplina) {
          cambios["disciplinaAnterior"] = antes.disciplina ?? null;
          cambios["disciplinaNueva"] = despues.disciplina ?? null;
        }
        if (Object.keys(cambios).length > 0) {
          void registrarAuditoria(tenantId, {
            usuario: _actorEmail ?? "desconocido",
            accion: cambios["rolNuevo"] ? "USUARIO_ROL_CAMBIADO" : "USUARIO_ACTUALIZADO",
            entidad: "Usuario",
            entidadId: despues.email || id,
            diff: cambios,
          });
        }
      }
      if (despues && antes && antes.rol !== despues.rol) {
        void notificarCambioRol({
          tenantId,
          usuario: despues,
          rolAnterior: antes.rol,
          actor: {
            email: _actorEmail ?? "admin",
            ...(_actorName !== undefined && { nombre: _actorName }),
          },
        });
      }
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["usuario", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["usuarios", tenantId] });
    },
  });
}

export function useSetActiveUsuario(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      buildRepos(tenantId).usuario.setActive(id, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["usuarios", tenantId] });
    },
  });
}

/**
 * Hard-delete de un usuario del módulo de Personal.
 *
 * **La información histórica del PMIS se preserva**: los campos
 * `createdBy`, `updatedBy`, `aprobadoPor`, `plId`, `backPlId`,
 * `solicitadoPor`, `comprador`, `reportadoPor`, etc. en AFE / SO / PR /
 * PO / RD / Acta / Riesgo / Stakeholder / Mejora / Auditoría son
 * **snapshots por email** (string plano), NO foreign keys al registro
 * `Usuario`. Eliminar el usuario solo le quita el ACCESO futuro al PMIS
 * — la trazabilidad histórica de qué hizo en el pasado queda íntegra.
 *
 * Consecuencias visibles tras eliminar:
 *   - El usuario deja de aparecer en `availableUsers` del login, en la
 *     matriz de permisos, en los selectores de PL/back-PL/comprador.
 *   - Los registros antiguos siguen mostrando su email (ya no se resuelve
 *     a nombre legible — se ve el email crudo, que es el comportamiento
 *     esperado para usuarios desvinculados).
 *
 * Solo el ADMIN_PMIS puede invocar este mutator (la UI lo gateaba).
 */
export function useDeleteUsuario(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buildRepos(tenantId).usuario.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["usuarios", tenantId] });
    },
  });
}

/**
 * Marca/desmarca al usuario como "default del PMIS" para su rol. La
 * mutación garantiza que solo haya UN default por rol en el tenant
 * (limpia los otros antes de marcar este).
 */
export function useSetDefaultUsuario(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      esDefault,
      userEmail,
    }: {
      id: string;
      esDefault: boolean;
      userEmail: string;
    }) => buildRepos(tenantId).usuario.setDefault(id, esDefault, userEmail),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["usuarios", tenantId] });
    },
  });
}
